import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { BookOpen, Video, Save, RotateCcw } from "lucide-react";

// Éditeur du carnet Petits Samouraïs : libellés, consignes, VIDÉOS de techniques par habileté, courriel auto au parent.
export function CurriculumEditor() {
  const [cur, setCur] = useState(null);
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const load = useCallback(async () => { try { const r = await api.get("/samourais/carnet/curriculum"); setCur(r.data.curriculum); setDirty(false); } catch { /* silencieux */ } }, []);
  useEffect(() => { load(); }, [load]);

  const setSkill = (si, ki, field, value) => {
    setCur((c) => { const n = structuredClone(c); n.stages[si].skills[ki][field] = value; return n; }); setDirty(true);
  };
  const save = async () => {
    try { await api.put("/samourais/carnet/curriculum", { curriculum: cur }); toast.success("Carnet enregistré ✓"); setDirty(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const reset = async () => {
    if (!window.confirm("Revenir au carnet par défaut ? (les vidéos saisies seront effacées)")) return;
    try { await api.delete("/samourais/carnet/curriculum"); toast.success("Carnet réinitialisé"); load(); } catch { toast.error("Erreur"); }
  };
  if (!cur) return null;
  const videos = cur.stages.reduce((n, s) => n + s.skills.filter((k) => k.video_url).length, 0);
  const total = cur.stages.reduce((n, s) => n + s.skills.length, 0);

  return (
    <div className="mb-6 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="ps-curriculum-panel">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="ps-curriculum-toggle">
        <div className="flex items-center gap-2 font-semibold"><BookOpen className="w-4 h-4 text-orange-400" /> Carnet du Petit Samouraï — habiletés & vidéos
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-zinc-300" data-testid="ps-curriculum-videos">🎬 {videos}/{total} vidéos</span>
        </div>
        <span className="text-xs text-zinc-400">{cur.stages.length} étapes · {open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="border-t border-white/10 p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="ps-curriculum-notify">
                <input type="checkbox" checked={cur.notify_parents !== false} onChange={(e) => { setCur({ ...cur, notify_parents: e.target.checked }); setDirty(true); }} className="accent-orange-500" />
                Courriel automatique « nouvelle habileté réussie » au parent (avec le badge) dès que le coach coche
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="ps-curriculum-weekly">
                <input type="checkbox" checked={cur.weekly_reminder !== false} onChange={(e) => { setCur({ ...cur, weekly_reminder: e.target.checked }); setDirty(true); }} className="accent-orange-500" />
                Défi duo du dimanche : courriel hebdo au parent avec l'habileté en cours et sa vidéo (dimanche ~17 h)
                <button onClick={async () => { if (!window.confirm("Envoyer MAINTENANT le défi duo de la semaine aux parents non encore servis cette semaine ?")) return; try { const r = await api.post("/samourais/carnet/weekly-video/run"); toast.success(`Envoyé : ${r.data.sent} · ignorés : ${r.data.skipped}`); } catch { toast.error("Erreur"); } }} className="ml-1 text-[11px] px-2 py-0.5 rounded bg-white/10 hover:bg-white/20" data-testid="ps-curriculum-weekly-run">Envoyer maintenant</button>
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={reset} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs flex items-center gap-1" data-testid="ps-curriculum-reset"><RotateCcw className="w-3 h-3" /> Par défaut</button>
              <button onClick={save} disabled={!dirty} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-xs font-semibold flex items-center gap-1" data-testid="ps-curriculum-save"><Save className="w-3 h-3" /> Enregistrer</button>
            </div>
          </div>
          <p className="text-[11px] text-zinc-400">Comme les carnets des autres disciplines : colle un lien YouTube / Vimeo / Bunny par habileté — la vidéo apparaît dans le carnet en ligne du parent et dans le courriel de réussite.</p>
          {cur.stages.map((st, si) => (
            <div key={st.id} className="rounded-xl border border-white/10 p-3" data-testid={`ps-curriculum-stage-${st.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <img src={st.badge} alt="" className="w-10 h-10 rounded-full" />
                <div><span className="font-bold" style={{ color: st.color }}>{st.name}</span> <span className="text-xs text-zinc-400">— {st.subtitle} · {st.certificate_title}</span></div>
              </div>
              <div className="space-y-2">
                {st.skills.map((k, ki) => (
                  <div key={k.id} className="grid sm:grid-cols-[1fr_1fr_1.2fr] gap-2 text-xs">
                    <input value={k.label} onChange={(e) => setSkill(si, ki, "label", e.target.value)} className="rounded bg-zinc-950 border border-white/10 px-2 py-1.5" data-testid={`ps-skill-label-${k.id}`} />
                    <input value={k.hint || ""} onChange={(e) => setSkill(si, ki, "hint", e.target.value)} placeholder="Consigne / repère pour le coach et le parent" className="rounded bg-zinc-950 border border-white/10 px-2 py-1.5" />
                    <div className="flex items-center gap-1">
                      <Video className={`w-3.5 h-3.5 shrink-0 ${k.video_url ? "text-emerald-400" : "text-zinc-600"}`} />
                      <input value={k.video_url || ""} onChange={(e) => setSkill(si, ki, "video_url", e.target.value)} placeholder="Lien vidéo de la technique (YouTube, Vimeo…)" className="flex-1 rounded bg-zinc-950 border border-white/10 px-2 py-1.5" data-testid={`ps-skill-video-${k.id}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
